// pages/purchase-orders/edit.jsx (EditPO.jsx)
import { useEffect, useMemo, useRef, useState } from "react"
import { useLoaderData, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { useSWRConfig } from "swr"
import { Transition } from "components"
import { usePage } from "contexts/page-context"
import { useAuth } from "contexts/auth-context"

// ✅ pakai Form kamu yang sudah stabil (yang ada ps_list supplier filter, dll)
import Form from "./form"

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// ✅ fetch helper (konsisten: credentials include)
async function apiFetch(path, { method = "GET", body } = {}) {
  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  const url = `${base}${path}`

  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : null
  })

  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = json?.message || "Request gagal"
    const err = json?.error ? `: ${json.error}` : ""
    throw new Error(msg + err)
  }
  return json
}

export default function EditPO() {
  const { id } = useParams() // /purchase-orders/:id/edit
  const isEdit = Boolean(id)

  const { can } = useAuth()
  const { cache } = useSWRConfig()
  const { setTransition } = usePage()
  const { setTitle } = useOutletContext()

  const location = useLocation()
  const navigate = useNavigate()

  // loader idealnya sudah ngasih data PO terbaru
  const loaderData = useLoaderData()
  // dukung beberapa kemungkinan bentuk:
  const poFromLoader = loaderData?.data || loaderData?.purchase_order || loaderData || null

  const [po, setPo] = useState(poFromLoader)
  const [errors, setErrors] = useState({})

  const status = String(po?.status || "DRAFT").toUpperCase()
  const isEditable = ["DRAFT", "REJECTED"].includes(status)
  const isLocked = !isEditable

  // ✅ guard supaya setTitle/transition gak “berulang” aneh
  const initRef = useRef(false)

  useEffect(() => {
    if (initRef.current) return
    initRef.current = true

    setTransition(location.state?.transition)
    setTitle(`Edit Purchase Order ${id || ""}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // kalau loader berubah (misal revalidation), update state
  useEffect(() => {
    setPo(poFromLoader)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poFromLoader?.id])

  const buildPayload = (payload) => {
    // payload dari Form (disarankan Form kirim object final)
    // fallback: kalau Form belum kirim items, pakai po.items
    const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(po?.items) ? po.items : []

    return {
      outlet_id: Number(payload?.outlet_id ?? po?.outlet_id),
      supplier_id: payload?.supplier_id ?? po?.supplier_id,
      po_date: payload?.po_date ?? po?.po_date ?? todayISO(),
      po_type: payload?.po_type ?? po?.po_type ?? "REGULAR",
      remarks: payload?.notes ?? payload?.remarks ?? po?.remarks ?? po?.notes ?? null,
      status: payload?.status,
      updatedBy:payload?.updatedBy,
      items: items.map((it) => ({
        product_unit_id: it.product_unit_id,
        uom: it.uom ?? "PCS",
        qty_ordered: num(it.qty_ordered),
        cost_price: num(it.cost_price),
      })),
    }
  }

  const validate = (payload) => {
    const e = {}
    if (!payload?.outlet_id) e.outlet_id = "Outlet wajib dipilih"
    if (!payload?.supplier_id) e.supplier_id = "Supplier wajib dipilih"
    if (!payload?.po_date) e.po_date = "Tanggal PO wajib diisi"

    const items = Array.isArray(payload?.items) ? payload.items : []
    if (!items.length) e.items = "Minimal 1 item"

    for (const [idx, it] of items.entries()) {
      if (!it.product_unit_id) e[`items.${idx}.product_unit_id`] = "Product wajib"
      if (num(it.qty_ordered) <= 0) e[`items.${idx}.qty_ordered`] = "Qty harus > 0"
      if (status !== "REJECTED" && num(it.cost_price) <= 0) {
        e[`items.${idx}.cost_price`] = "Cost price harus > 0"
      }
      if (!it.uom) e[`items.${idx}.uom`] = "UOM wajib"
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  // =========================
  // Actions (container handles)
  // =========================

  const handleSaveDraft = async (formPayload) => {
    if (isLocked) return

    const payload = buildPayload(formPayload)
    if (!validate(payload)) return

    try {
      nProgress.start()
      toast.loading("Saving draft...", { id: "po-save" })

      const res = await apiFetch(`/purchase-orders/${id}`, {
        method: "PUT",
        body: payload,
      })

      toast.success(res?.message || "PO berhasil disimpan", { id: "po-save" })

      // refresh state kalau API balikin data
      const nextPo = res?.data || res
      if (nextPo?.id) setPo(nextPo)

      cache?.clear?.()
    } catch (e) {
      toast.error(e?.message || "Gagal save PO", { id: "po-save" })
    } finally {
      nProgress.done()
    }
  }

  const handleApprove = async () => {
    if (!isEdit) return
    if (isLocked) return
    if (!can?.("purchase-order.approve")) return

    const ok = window.confirm(`Approve PO ${po?.po_number || id || ""}?`)
    if (!ok) return

    try {
      nProgress.start()
      toast.loading("Approving...", { id: "po-approve" })

      const res = await apiFetch(`/purchase-orders/${id}/approve`, { method: "POST" })

      toast.success(res?.message || "PO berhasil di-approve", { id: "po-approve" })

      // update local status
      setPo((prev) => ({ ...(prev || {}), status: "APPROVED" }))

      cache?.clear?.()
    } catch (e) {
      toast.error(e?.message || "Gagal approve", { id: "po-approve" })
    } finally {
      nProgress.done()
    }
  }

  const handleCancel = async () => {
    if (!isEdit) return
    if (!can?.("purchase-order.cancel")) return
    if (status === "CANCELLED") return

    const reason = window.prompt("Alasan cancel (optional):", "")
    if (reason === null) return

    try {
      nProgress.start()
      toast.loading("Cancelling...", { id: "po-cancel" })

      const res = await apiFetch(`/purchase-orders/${id}/cancel`, {
        method: "POST",
        body: reason ? { reason } : null,
      })

      toast.success(res?.message || "PO berhasil dibatalkan", { id: "po-cancel" })

      setPo((prev) => ({ ...(prev || {}), status: "CANCELLED" }))
      cache?.clear?.()
    } catch (e) {
      toast.error(e?.message || "Gagal cancel", { id: "po-cancel" })
    } finally {
      nProgress.done()
    }
  }

  const headerActions = useMemo(
    () => ({
      canCancel: Boolean(can?.("purchase-order.cancel")) && status !== "CANCELLED",
      canApprove: Boolean(can?.("purchase-order.approve")) && status === "DRAFT",
      canSave: Boolean(can?.("purchase-order.edit")) || Boolean(can?.("purchase-order.create")),
      isLocked,
      status,
      onCancel: handleCancel,
      onApprove: handleApprove,
      onSaveDraft: handleSaveDraft,
      onBack: () => navigate("/purchase-orders", { state: location.state }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can, status, isLocked, id, po?.po_number]
  )

  return (
    <Transition type="fade">
      {/* 
        ✅ Form tetap “dumb”, cuma render + kumpulin state.
        Form cukup panggil onSubmit(payload) ketika klik Save Draft.
        Approve/Cancel tombol bisa kamu taruh di Form header, tapi action-nya dari container.
      */}
      <Form
        data={po}
        errors={errors}
        actions={headerActions}
        onSubmit={handleSaveDraft}
      />
    </Transition>
  )
}
